import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';

import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { ApiTopupService } from '../../../../core/services/api/cityparking/api-topup.service';
import { TopupMapper } from '../../../../core/mappers/cityparking/topup.mapper';
import { TopupAccount, TopupTransaction } from '../../../../core/models/cityparking/topup.model';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalTopupDetailComponent } from '../../modals/cityparking/topup/modal-topup-detail/modal-topup-detail.component';

// Custom Date Format for Displaying DD/MM/YYYY in Datepicker
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  }
};

@Component({
  selector: 'app-top-up',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    PssLoadingComponent,
    ModalTopupDetailComponent
  ],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'th-TH' }
  ],
  templateUrl: './top-up.component.html',
  styleUrl: './top-up.component.css'
})
export class TopUpComponent implements OnInit {
  private readonly apiService = inject(ApiTopupService);
  private readonly mapper = inject(TopupMapper);
  private readonly cookieService = inject(CookieService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  // ================= State Variables =================
  viewMode: 'accounts' | 'transactions' = 'accounts';
  isLoading: boolean = false;
  userId: number = 0;
  menuId: number = 0;

  // --- Accounts Tab States ---
  searchText: string = '';
  searchField: string = 'all';
  isSearchTypeDropdownOpen: boolean = false;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  currentPage: number = 1;
  pageSize: number = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems: number = 0;
  totalPages: number = 0;
  accounts: TopupAccount[] = [];
  isPageSizeDropdownOpen: boolean = false;

  // --- Modal Integration States ---
  selectedAccountId: number | null = null;
  isDetailModalOpen: boolean = false;

  // --- Global Transactions Tab States ---
  txStartDate: Date = new Date();
  txEndDate: Date = new Date();
  txSearchText: string = '';
  txSearchField: string = 'all';
  isTxSearchDropdownOpen: boolean = false;
  txMethodFilter: number | null = null;
  isMethodFilterDropdownOpen: boolean = false;
  txMinAmount: number | null = null;
  txMaxAmount: number | null = null;
  showTableFilters: boolean = false;

  txCurrentPage: number = 1;
  txPageSize: number = 20;
  txTotalItems: number = 0;
  txTotalPages: number = 0;
  globalTransactions: TopupTransaction[] = [];
  isTxPageSizeDropdownOpen: boolean = false;

  // ================= Normal Functions =================

  /**
   * Initializes state cookie credentials, default filters and fetches accounts
   */
  ngOnInit(): void {
    this.userId = Number(this.cookieService.get('user_id') || 0);
    this.menuId = this.permissionService.getMenuId(this.router.url) || 0;

    // Set default Date Range for Global Transactions to past 7 days
    const past7Days = new Date();
    past7Days.setDate(past7Days.getDate() - 7);
    this.txStartDate = past7Days;
    this.txEndDate = new Date();

    this.loadAccounts();
  }

  /**
   * Switches component view mode between accounts list and global transactions list
   * @param mode Target view mode
   */
  switchViewMode(mode: 'accounts' | 'transactions'): void {
    this.viewMode = mode;
    if (mode === 'accounts') {
      this.loadAccounts();
    } else if (mode === 'transactions') {
      this.loadGlobalTransactions();
    }
  }

  /**
   * Toggle search column options selection dropdown for accounts list
   */
  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  /**
   * Update accounts search target field column and refresh list
   * @param type Target search field column type
   */
  selectSearchType(type: string): void {
    this.searchField = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  /**
   * Display text label matching active search column
   */
  get currentSearchTypeLabel(): string {
    switch (this.searchField) {
      case 'topupnumber': return 'เลขบัญชี';
      case 'fullname': return 'ชื่อผู้ใช้';
      case 'phone': return 'เบอร์โทร';
      default: return 'ทั้งหมด';
    }
  }

  /**
   * Reset pagination index and reload search criteria matching accounts list
   */
  onSearchChange(): void {
    this.currentPage = 1;
    this.loadAccounts();
  }

  /**
   * Order accounts table data by column and toggle sort direction
   * @param column Target data sort column name
   */
  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadAccounts();
  }

  /**
   * Toggle search column options selection dropdown for global transactions list
   */
  toggleTxSearchDropdown(): void {
    this.isTxSearchDropdownOpen = !this.isTxSearchDropdownOpen;
  }

  /**
   * Update transactions search target field column and refresh list
   * @param type Target search field column type
   */
  selectTxSearchType(type: string): void {
    this.txSearchField = type;
    this.isTxSearchDropdownOpen = false;
    this.onTxSearchChange();
  }

  /**
   * Display text label matching active transactions search column
   */
  get currentTxSearchTypeLabel(): string {
    switch (this.txSearchField) {
      case 'topupnumber': return 'เลขบัญชี';
      case 'fullname': return 'ชื่อผู้ใช้';
      case 'phone': return 'เบอร์โทร';
      default: return 'ทั้งหมด';
    }
  }

  /**
   * Reset pagination index and reload search criteria matching transactions list
   */
  onTxSearchChange(): void {
    this.txCurrentPage = 1;
    this.loadGlobalTransactions();
  }

  /**
   * Display text label matching active method filter
   */
  get methodFilterLabel(): string {
    switch (this.txMethodFilter) {
      case 1: return 'เติมเงิน (Top Up)';
      case 2: return 'ชำระด้วยยอด Topup';
      case 3: return 'ปรับยอดเพิ่ม (Backoffice)';
      case 4: return 'ปรับยอดลด (Backoffice)';
      default: return 'ทั้งหมด';
    }
  }

  /**
   * Toggle method filter dropdown list
   */
  toggleMethodFilterDropdown(): void {
    this.isMethodFilterDropdownOpen = !this.isMethodFilterDropdownOpen;
  }

  /**
   * Safely dismiss method filter dropdown list
   */
  closeMethodFilterDropdown(): void {
    setTimeout(() => {
      this.isMethodFilterDropdownOpen = false;
    }, 200);
  }

  /**
   * Update selected method filter and refresh list
   * @param method Target method value
   */
  selectMethodFilter(method: number | null): void {
    this.txMethodFilter = method;
    this.isMethodFilterDropdownOpen = false;
    this.onTxSearchChange();
  }

  /**
   * Set target account details ID and open the modal display
   * @param id Account target record ID
   */
  viewAccountDetail(id: number): void {
    this.selectedAccountId = id;
    this.isDetailModalOpen = true;
  }

  /**
   * Reload parent accounts list table on successful modal balance adjustments
   */
  onDetailSaveSuccess(): void {
    this.loadAccounts();
  }

  /**
   * Toggle page size selection dropdown list for accounts tab
   */
  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  /**
   * Safely dismiss page size selection dropdown list for accounts tab
   */
  closePageSizeDropdown(): void {
    setTimeout(() => {
      this.isPageSizeDropdownOpen = false;
    }, 200);
  }

  /**
   * Select pagination page size limit for accounts list
   * @param size Number of items to display
   */
  selectPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.isPageSizeDropdownOpen = false;
    this.loadAccounts();
  }

  /**
   * Navigate to target page of accounts list
   * @param page Target page index
   */
  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAccounts();
    }
  }

  /**
   * Starting index number of current page items display range for accounts list
   */
  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  /**
   * Ending index number of current page items display range for accounts list
   */
  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  /**
   * Numeric page index options array list to render in accounts list footer
   */
  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /**
   * Toggle page size selection dropdown list for global transactions tab
   */
  toggleTxPageSizeDropdown(): void {
    this.isTxPageSizeDropdownOpen = !this.isTxPageSizeDropdownOpen;
  }

  /**
   * Safely dismiss page size selection dropdown list for global transactions tab
   */
  closeTxPageSizeDropdown(): void {
    setTimeout(() => {
      this.isTxPageSizeDropdownOpen = false;
    }, 200);
  }

  /**
   * Select pagination page size limit for global transactions list
   * @param size Number of items to display
   */
  selectTxPageSize(size: number): void {
    this.txPageSize = size;
    this.txCurrentPage = 1;
    this.isTxPageSizeDropdownOpen = false;
    this.loadGlobalTransactions();
  }

  /**
   * Navigate to target page of global transactions list
   * @param page Target page index
   */
  setTxPage(page: number): void {
    if (page >= 1 && page <= this.txTotalPages) {
      this.txCurrentPage = page;
      this.loadGlobalTransactions();
    }
  }

  /**
   * Starting index number of current page items display range for global transactions list
   */
  get txStartItemIndex(): number {
    if (this.txTotalItems === 0) return 0;
    return (this.txCurrentPage - 1) * this.txPageSize + 1;
  }

  /**
   * Ending index number of current page items display range for global transactions list
   */
  get txEndItemIndex(): number {
    return Math.min(this.txCurrentPage * this.txPageSize, this.txTotalItems);
  }

  /**
   * Numeric page index options array list to render in global transactions list footer
   */
  get txPagesArray(): number[] {
    const total = this.txTotalPages;
    const maxVisible = 5;
    let start = Math.max(1, this.txCurrentPage - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  /**
   * Format Javascript Date instance to Timezone safe ISO YYYY-MM-DD string for service API requests
   * @param date Javascript Date instance
   */
  private formatDateForApi(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  // ================= API Functions =================

  /**
   * Load accounts matching filter query parameters from service API
   */
  loadAccounts(): void {
    this.isLoading = true;
    const query = {
      userId: this.userId,
      menuId: this.menuId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.searchField : undefined,
      searchText: this.searchText.trim() || undefined
    };

    this.apiService.getTopupAccountForTable(query).subscribe({
      next: (res) => {
        console.log("getTopupAccountForTable", res);
        this.isLoading = false;
        const mapped = this.mapper.mapAccountTable(res);
        this.accounts = mapped.data;
        this.totalItems = mapped.total;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดข้อมูลบัญชี Topup ได้');
        console.error(err);
      }
    });
  }

  /**
   * Load global transaction histories matching filter query parameters from service API
   */
  loadGlobalTransactions(): void {
    this.isLoading = true;
    const query = {
      startDate: this.formatDateForApi(this.txStartDate),
      endDate: this.formatDateForApi(this.txEndDate),
      page: this.txCurrentPage,
      pageSize: this.txPageSize,
      topupMethod: this.txMethodFilter !== null ? this.txMethodFilter : undefined,
      minAmount: this.txMinAmount !== null ? this.txMinAmount : undefined,
      maxAmount: this.txMaxAmount !== null ? this.txMaxAmount : undefined,
      searchField: this.txSearchText.trim() ? this.txSearchField : undefined,
      searchText: this.txSearchText.trim() || undefined
    };

    this.apiService.getTopupTransactionForTable(query).subscribe({
      next: (res) => {
        console.log("getTopupTransactionForTable", res);
        this.isLoading = false;
        const mapped = this.mapper.mapTransactionTable(res);
        this.globalTransactions = mapped.data;
        this.txTotalItems = mapped.total;
        this.txTotalPages = Math.ceil(this.txTotalItems / this.txPageSize) || 1;
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดประวัติรายการธุรกรรมได้');
        console.error(err);
      }
    });
  }
}
